import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, AlertTriangle, ShoppingBag, Home, RefreshCw } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PaymentStatus = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('pending');
  const [orderData, setOrderData] = useState(null);
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    if (location.state) {
      setStatus(location.state.status || 'pending');
      setOrderData(location.state.orderData || null);
    } else {
      navigate('/store');
    }
  }, [location, navigate]);

  // Auto-redirect after 5 seconds for success/completed status
  useEffect(() => {
    if (status === 'success' || status === 'completed') {
      if (countdown <= 0) {
        navigate('/store');
        return;
      }
      const timer = setTimeout(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [status, countdown, navigate]);

  const getStatusConfig = () => {
    switch (status) {
      case 'success':
      case 'completed':
        return { icon: CheckCircle, iconColor: 'text-green-500', bgColor: 'bg-green-50', borderColor: 'border-green-200', textColor: 'text-green-800', iconBg: 'bg-green-100', title: 'Payment Successful!', subtitle: `Your order has been placed successfully. Redirecting to store in ${countdown}s...` };
      case 'failed':
        return { icon: XCircle, iconColor: 'text-red-500', bgColor: 'bg-red-50', borderColor: 'border-red-200', textColor: 'text-red-800', iconBg: 'bg-red-100', title: 'Payment Failed', subtitle: 'Your payment could not be processed. Please try again.' };
      case 'cancelled':
        return { icon: AlertTriangle, iconColor: 'text-yellow-500', bgColor: 'bg-yellow-50', borderColor: 'border-yellow-200', textColor: 'text-yellow-800', iconBg: 'bg-yellow-100', title: 'Payment Cancelled', subtitle: 'You cancelled the payment.' };
      default:
        return { icon: Clock, iconColor: 'text-orange-500', bgColor: 'bg-orange-50', borderColor: 'border-orange-200', textColor: 'text-orange-800', iconBg: 'bg-orange-100', title: 'Payment Pending', subtitle: 'Your payment is being processed.' };
    }
  };

  const config = getStatusConfig();
  const StatusIcon = config.icon;
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50">
      <Header />
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className={`${config.bgColor} ${config.borderColor} border-2 rounded-2xl p-8 text-center mb-6`}>
          <div className={`${config.iconBg} rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-4`}>
            <StatusIcon size={64} className={config.iconColor} />
          </div>
          <h1 className={`text-2xl font-bold ${config.textColor} mb-2`}>{config.title}</h1>
          <p className="text-gray-600">{config.subtitle}</p>
        </div>
        {orderData && (
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">Order Details</h2>
            <div className="space-y-3 border-b border-gray-200 pb-4 mb-4">
              <div className="flex justify-between">
                <span className="text-gray-500">Order ID</span>
                <span className="font-mono text-sm font-medium">{orderData._id || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Order Date</span>
                <span className="font-medium">{formatDate(orderData.createdAt || orderData.paidAt)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Payment Method</span>
                <span className="font-medium">{orderData.paymentMethod || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Payment Status</span>
                <span className={`font-medium ${orderData.isPaid ? 'text-green-600' : 'text-yellow-600'}`}>{orderData.isPaid ? 'Paid' : 'Pending'}</span>
              </div>
              {orderData.paymentResult && orderData.paymentResult.id && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Transaction ID</span>
                  <span className="font-mono text-xs font-medium truncate max-w-[200px]">{orderData.paymentResult.id}</span>
                </div>
              )}
            </div>
            {orderData.orderItems && orderData.orderItems.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Items Ordered</h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {orderData.orderItems.map((item, index) => (
                    <div key={index} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                      {item.image && <img src={item.image} alt={item.name} className="w-12 h-12 object-cover rounded-lg" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{item.name}</p>
                        <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                      </div>
                      <span className="text-sm font-semibold text-purple-700">Rs {Math.round(item.price * item.quantity)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="border-t border-gray-200 pt-4 space-y-2">
              {orderData.itemsPrice > 0 && <div className="flex justify-between text-sm text-gray-600"><span>Subtotal</span><span>Rs {Math.round(orderData.itemsPrice)}</span></div>}
              {orderData.couponDiscount > 0 && <div className="flex justify-between text-sm text-green-600"><span>Discount</span><span>-Rs {Math.round(orderData.couponDiscount)}</span></div>}
              {orderData.shippingPrice > 0 && <div className="flex justify-between text-sm text-gray-600"><span>Shipping</span><span>Rs {Math.round(orderData.shippingPrice)}</span></div>}
              {orderData.taxPrice > 0 && <div className="flex justify-between text-sm text-gray-600"><span>Tax</span><span>Rs {Math.round(orderData.taxPrice)}</span></div>}
              {orderData.giftPackingPrice > 0 && <div className="flex justify-between text-sm text-purple-600"><span>Gift Packing</span><span>Rs {Math.round(orderData.giftPackingPrice)}</span></div>}
              <div className="flex justify-between text-lg font-bold text-gray-800 pt-2 border-t border-gray-200">
                <span>Total</span>
                <span className="text-purple-600">Rs {Math.round(orderData.totalPrice || 0)}</span>
              </div>
            </div>
            {orderData.shippingAddress && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Shipping Address</h3>
                <p className="text-sm text-gray-600">{orderData.shippingAddress.street}, {orderData.shippingAddress.city}, {orderData.shippingAddress.state} - {orderData.shippingAddress.zipCode}</p>
              </div>
            )}
          </div>
        )}
        <div className="space-y-3">
          {status === 'failed' && (
            <button onClick={() => navigate('/checkout')} className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-xl hover:shadow-lg transition-all font-medium flex items-center justify-center gap-2">
              <RefreshCw size={20} /> Retry Payment
            </button>
          )}
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => navigate('/my-orders')} className="w-full bg-white border-2 border-purple-200 text-purple-700 py-3 rounded-xl hover:bg-purple-50 transition-all font-medium flex items-center justify-center gap-2">
              <ShoppingBag size={20} /> My Orders
            </button>
            <button onClick={() => navigate('/store')} className="w-full bg-white border-2 border-gray-200 text-gray-700 py-3 rounded-xl hover:bg-gray-50 transition-all font-medium flex items-center justify-center gap-2">
              <Home size={20} /> Continue Shopping
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PaymentStatus;

